import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Plus, Trash2, MailOpen, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState({});
  const [sending, setSending] = useState(null);
  const [newTitre, setNewTitre] = useState("");
  const [newTexte, setNewTexte] = useState("");
  const [showTemplates, setShowTemplates] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/messages-joueurs`, { withCredentials: true }).then((r) => setMessages(r.data)).catch(() => {});
    axios.get(`${API}/admin/reponses-types`, { withCredentials: true }).then((r) => setTemplates(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const ouvrir = async (m) => {
    setOpen(open === m.id ? null : m.id);
    if (open !== m.id && !m.lu) {
      await axios.post(`${API}/admin/messages-joueurs/${m.id}/lu`, {}, { withCredentials: true }).catch(() => {});
      setMessages((ms) => ms.map((x) => (x.id === m.id ? { ...x, lu: true } : x)));
    }
  };

  const repondre = async (m) => {
    const texte = (reply[m.id] || "").trim();
    if (!texte) return;
    setSending(m.id);
    try {
      await axios.post(`${API}/admin/messages-joueurs/${m.id}/repondre`, { texte }, { withCredentials: true });
      toast.success(`Réponse envoyée à ${m.joueur} (portail + email + push)`);
      setReply((r) => ({ ...r, [m.id]: "" }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setSending(null);
    }
  };

  const ajouterTemplate = async () => {
    if (!newTitre.trim() || !newTexte.trim()) return;
    try {
      await axios.post(`${API}/admin/reponses-types`, { titre: newTitre, texte: newTexte }, { withCredentials: true });
      toast.success("Modèle enregistré");
      setNewTitre("");
      setNewTexte("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const supprimerTemplate = async (id) => {
    await axios.delete(`${API}/admin/reponses-types/${id}`, { withCredentials: true }).catch(() => {});
    load();
  };

  const nonLus = messages.filter((m) => !m.lu).length;

  return (
    <div data-testid="admin-messages" className="max-w-4xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-[#71717A]">
          Messages envoyés par les joueurs depuis leur portail. {nonLus > 0 && <strong className="text-[#C5A059]">{nonLus} non lu{nonLus > 1 ? "s" : ""}</strong>}
        </p>
        <button onClick={() => setShowTemplates(!showTemplates)} data-testid="messages-templates-toggle" className="text-[10px] uppercase tracking-[0.2em] border border-[#27272A] px-4 py-2.5 text-[#A1A1AA] hover:text-[#C5A059] hover:border-[#C5A059] transition-colors">
          Réponses pré-enregistrées ({templates.length})
        </button>
      </div>

      {showTemplates && (
        <div className="border border-[#27272A] bg-[#121214] p-6 space-y-4" data-testid="messages-templates-panel">
          <p className="text-[10px] uppercase tracking-widest text-[#71717A]">Modèles de réponse réutilisables (gestion réservée à l'admin)</p>
          {templates.map((t) => (
            <div key={t.id} className="flex items-start justify-between gap-4 border-b border-[#27272A]/60 pb-3">
              <div>
                <p className="text-white text-sm font-medium">{t.titre}</p>
                <p className="text-[#71717A] text-xs mt-1 leading-relaxed">{t.texte}</p>
              </div>
              <button onClick={() => supprimerTemplate(t.id)} data-testid={`template-delete-${t.id.slice(0, 8)}`} className="text-[#71717A] hover:text-red-400 transition-colors p-1" aria-label="Supprimer"><Trash2 size={14} /></button>
            </div>
          ))}
          <div className="grid sm:grid-cols-[220px_1fr_auto] gap-3 pt-2">
            <input value={newTitre} onChange={(e) => setNewTitre(e.target.value)} placeholder="Titre (ex : Convocation)" data-testid="template-new-titre" className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A059]" />
            <input value={newTexte} onChange={(e) => setNewTexte(e.target.value)} placeholder="Texte de la réponse…" data-testid="template-new-texte" className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A059]" />
            <button onClick={ajouterTemplate} data-testid="template-add" className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-4 py-2 text-[10px] uppercase tracking-wider font-semibold hover:bg-[#E5C07B] transition-colors">
              <Plus size={13} /> Ajouter
            </button>
          </div>
        </div>
      )}

      {messages.length === 0 && (
        <p className="border border-[#27272A] bg-[#121214] p-10 text-center text-sm text-[#71717A]">Aucun message de joueur pour le moment.</p>
      )}
      {messages.map((m, i) => (
        <div key={m.id} data-testid={`message-card-${i}`} className={`border bg-[#121214] ${m.lu ? "border-[#27272A]" : "border-[#C5A059]/60"}`}>
          <button onClick={() => ouvrir(m)} data-testid={`message-open-${i}`} className="w-full text-left px-6 py-4 flex flex-wrap items-center gap-3 hover:bg-[#0A0A0C] transition-colors">
            {!m.lu ? <MailOpen size={15} className="text-[#C5A059] shrink-0" /> : <MessageSquare size={15} className="text-[#71717A] shrink-0" />}
            <div className="flex-1 min-w-[200px]">
              <p className="text-white text-sm font-medium">{m.joueur} <span className="text-[#71717A] font-normal">· {m.reference}</span></p>
              <p className="text-[#C5A059] text-xs mt-0.5">{m.sujet}</p>
            </div>
            <span className="text-[#3f3f46] text-[10px]">{new Date(m.created_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
            {(m.reponses || []).length > 0 && <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 border border-emerald-400/40 text-emerald-300">{m.reponses.length} rép.</span>}
          </button>
          {open === m.id && (
            <div className="px-6 pb-6 border-t border-[#27272A]/60">
              <p className="text-sm text-[#A1A1AA] leading-relaxed py-4 whitespace-pre-wrap">{m.message}</p>
              {(m.reponses || []).map((r, j) => (
                <div key={j} className="border-l-2 border-[#C5A059] bg-[#0A0A0C] px-4 py-3 mb-2" data-testid={`message-reponse-${i}-${j}`}>
                  <p className="text-[9px] uppercase tracking-widest text-[#C5A059]">Réponse du staff · {r.par} · {new Date(r.at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                  <p className="text-sm text-white mt-1 whitespace-pre-wrap">{r.texte}</p>
                </div>
              ))}
              <div className="mt-4 space-y-3">
                {templates.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => {
                      const tpl = templates.find((t) => t.id === e.target.value);
                      if (tpl) setReply((r) => ({ ...r, [m.id]: tpl.texte }));
                    }}
                    data-testid={`message-template-select-${i}`}
                    className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2 text-xs focus:outline-none focus:border-[#C5A059] w-full sm:w-auto"
                  >
                    <option value="">Insérer une réponse pré-enregistrée…</option>
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.titre}</option>)}
                  </select>
                )}
                <textarea
                  rows={3}
                  value={reply[m.id] || ""}
                  onChange={(e) => setReply((r) => ({ ...r, [m.id]: e.target.value }))}
                  placeholder={`Répondre à ${m.joueur}…`}
                  data-testid={`message-reply-textarea-${i}`}
                  className="block w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A059] resize-none"
                />
                <button
                  onClick={() => repondre(m)}
                  disabled={sending === m.id || !(reply[m.id] || "").trim()}
                  data-testid={`message-reply-send-${i}`}
                  className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors disabled:opacity-50"
                >
                  {sending === m.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                  Répondre (portail + email)
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
